/* Derive touch feedback from the theme/app hover rules instead of restyling controls. */
(() => {
  const properties=new Set(['background','background-color','background-image','color','border','border-color','border-top-color','border-right-color','border-bottom-color','border-left-color','box-shadow','text-shadow','transform','translate','scale','rotate','opacity','filter','outline','outline-color','outline-offset','text-decoration','text-decoration-color','fill','stroke']);
  const sheet=document.createElement('style');sheet.id='MeriavoTouchHover';document.head.append(sheet);
  const hoverMedia=matchMedia('(hover:hover) and (pointer:fine)'),originalSelectors=new WeakMap();
  const touchMedia=matchMedia('(any-pointer:coarse)');
  const reducedMotionMedia=matchMedia('(prefers-reduced-motion:reduce)');
  let touchObserved=false;
  const needsTouchRules=()=>touchObserved||touchMedia.matches||!hoverMedia.matches;
  let hoverEnabled;
  const mouseHover=enabled=>{if(hoverEnabled===enabled)return;hoverEnabled=enabled;document.documentElement.toggleAttribute('data-meriavo-hover',enabled);};
  mouseHover(hoverMedia.matches);hoverMedia.addEventListener('change',()=>mouseHover(hoverMedia.matches));
  const readRules=(rules,records=null,parent=[])=>{
    let out='';
    for(let index=0;index<rules.length;index++){const rule=rules[index];
      if(rule.selectorText?.includes(':hover')){
        const selector=originalSelectors.get(rule)||rule.selectorText;if(records)records.push({path:[...parent,index],selector});
        if(!originalSelectors.has(rule)){originalSelectors.set(rule,selector);rule.selectorText=selector.replace(/:hover\b/g,':hover:where(html[data-meriavo-hover] *)');}
        let declarations='';for(const name of rule.style)if(properties.has(name))declarations+=name+':'+rule.style.getPropertyValue(name)+(rule.style.getPropertyPriority(name)?'!important':'')+';';
        // FEATURE TOUCH-FEEDBACK: native press paints before click; the existing release pulse never delays activation.
        if(declarations)out+=selector.replace(/:hover\b/g,':is(:active,[data-touch-hover])')+'{'+declarations+'}\n';
      }else if(rule.cssRules){
        const inner=readRules(rule.cssRules,records,records?[...parent,index]:parent);if(!inner)continue;
        if(rule.conditionText&&rule.constructor.name==='CSSMediaRule'){
          const condition=rule.conditionText.replace(/\((?:any-)?hover:\s*hover\)|\(pointer:\s*fine\)/g,'(min-width:0px)');
          out+='@media '+condition+'{'+inner+'}';
        }else if(rule.conditionText&&rule.constructor.name==='CSSSupportsRule')out+='@supports '+rule.conditionText+'{'+inner+'}';
      }
    }
    return out;
  };
  const persistentPrefix="meriavo-touch-css-v2:";
  const persistentKey=source=>{
    if(!source.href)return null;
    const url=new URL(source.href,location.href);
    return url.origin===location.origin&&/\/cdn\/shop\/t\/\d+\/assets\/[^/]+\.css$/.test(url.pathname)&&url.searchParams.has("v")?persistentPrefix+url.href:null;
  };
  const compileSheet=(source,changed)=>{
    const rules=source.cssRules,key=persistentKey(source);
    if(key&&!changed){
      try{
        const saved=JSON.parse(sessionStorage.getItem(key)||"null");
        if(saved&&saved.length===rules.length&&typeof saved.css==="string"&&Array.isArray(saved.records)&&saved.records.length<=1000){
          const resolved=saved.records.map(record=>{
            if(!Array.isArray(record.path)||!record.path.length||typeof record.selector!=="string")throw new Error("Invalid stylesheet index");
            let list=rules,rule;
            for(let index=0;index<record.path.length;index++){
              const position=record.path[index];
              if(!Number.isInteger(position)||position<0||position>=list.length)throw new Error("Stale stylesheet index");
              rule=list[position];
              if(index<record.path.length-1)list=rule.cssRules;
            }
            if(!rule||(originalSelectors.get(rule)||rule.selectorText)!==record.selector)throw new Error("Changed stylesheet selector");
            return [rule,record.selector];
          });
          for(const [rule,selector]of resolved){
            if(!originalSelectors.has(rule)){
              originalSelectors.set(rule,selector);
              rule.selectorText=selector.replace(/:hover\b/g,":hover:where(html[data-meriavo-hover] *)");
            }
          }
          return saved.css;
        }
      }catch{}
    }
    // GPT WORK: Cache paths are only needed for versioned external sheets; avoid per-rule arrays elsewhere.
    const records=key?[]:null,css=readRules(rules,records);
    if(key){
      try{
        const value=JSON.stringify({length:rules.length,records,css});
        if(value.length<=150000){
          const keys=Object.keys(sessionStorage).filter(name=>name.startsWith(persistentPrefix));
          while(keys.length>=8&&!keys.includes(key))sessionStorage.removeItem(keys.shift());
          sessionStorage.setItem(key,value);
        }
      }catch{}
    }
    return css;
  };
  let frame=0;
  const cache=new WeakMap(),dirty=new WeakSet();
  let applied=[],incremental=true;
  const applyRules=entries=>{
    if(incremental){
      try{
        // Retain unchanged rule groups, including their cascade position.
        // Late app styles should not reparse every existing touch rule.
        const target=sheet.sheet;
        let start=0,oldEnd=applied.length,newEnd=entries.length;
        const same=(a,b)=>a.source===b.source&&a.css===b.css;
        while(start<oldEnd&&start<newEnd&&same(applied[start],entries[start]))start++;
        while(oldEnd>start&&newEnd>start&&same(applied[oldEnd-1],entries[newEnd-1])){oldEnd--;newEnd--;}
        for(let index=oldEnd-1;index>=start;index--)target.deleteRule(index);
        for(let index=start;index<newEnd;index++)target.insertRule('@media(hover:none),(pointer:coarse){'+entries[index].css+'}',index);
        applied=entries;return;
      }catch{incremental=false;}
    }
    // Preserve the original rendering path if CSSOM insertion is unavailable.
    const value='@media(hover:none),(pointer:coarse){'+entries.map(entry=>entry.css).join('')+'}';
    if(sheet.textContent!==value)sheet.textContent=value;
  };
  // GPT WORK: a direct load/first-touch rebuild replaces any queued rebuild.
  // Cancel that pending frame so the same stylesheet set is not processed twice.
  const rebuild=()=>{if(frame)cancelAnimationFrame(frame);frame=0;if(!needsTouchRules())return;const entries=[];for(const source of document.styleSheets){
    if(source.ownerNode===sheet)continue;
    let compiled=cache.get(source);
    if(compiled===undefined||dirty.has(source.ownerNode)){
      try{compiled=compileSheet(source,dirty.has(source.ownerNode));}catch{continue;}
      cache.set(source,compiled);dirty.delete(source.ownerNode);
    }
    if(compiled)entries.push({source,css:compiled});
  }
    applyRules(entries);
  };
  const schedule=()=>{if(needsTouchRules()&&!frame)frame=requestAnimationFrame(rebuild);};
  touchMedia.addEventListener('change',schedule);
  hoverMedia.addEventListener('change',schedule);
  const styleNode=node=>node?.nodeType===1&&node.matches('style,link[rel="stylesheet"]');
  new MutationObserver(records=>{
    let changed=false;
    for(const record of records){
      const owner=record.target.nodeType===1?record.target:record.target.parentElement;
      if(owner===sheet||sheet.contains(owner))continue;
      if(styleNode(owner)){dirty.add(owner);changed=true;}
      for(const node of [...record.addedNodes,...record.removedNodes])if(styleNode(node)&&node!==sheet)changed=true;
    }
    if(changed)schedule();
  }).observe(document.head,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['href','media','disabled']});
  document.addEventListener('load',event=>{if(styleNode(event.target))schedule();},true);
  window.addEventListener('load',rebuild,{once:true});rebuild();
  let active=[],releaseTimer=0,restoreTimer=0,pointer=null,pendingTap=null;
  const restore=()=>{clearTimeout(restoreTimer);for(const {node,transition}of active){node.removeAttribute('data-touch-hover');if(transition){if(transition[0])node.style.setProperty('transition',...transition);else node.style.removeProperty('transition');}}active=[];};
  const clear=(immediate=false)=>{
    clearTimeout(releaseTimer);pointer=null;pendingTap=null;active.forEach(({node})=>node.removeAttribute('data-touch-hover'));
    if(immediate)restore();else restoreTimer=setTimeout(restore,240);
  };
  const reset=()=>{clear(true);document.querySelectorAll('[data-touch-hover]').forEach(node=>node.removeAttribute('data-touch-hover'));};
  // Commit native activation first: hover-driven DOM/style changes must not consume the first tap.
  const activate=control=>{
    const pending=[];
    for(let node=control;node&&node!==document.body;node=node.parentElement){
      let transition=null;
      if(!node.closest('.hero-media-deck,[data-hero-deck]')&&getComputedStyle(node).transitionDuration.split(',').every(v=>parseFloat(v)===0)&&!reducedMotionMedia.matches){
        transition=[node.style.getPropertyValue('transition'),node.style.getPropertyPriority('transition')];

      }
      pending.push({node,transition});
    }
    for(const entry of pending){const {node,transition}=entry;if(transition)node.style.setProperty('transition','background-color 160ms ease,border-color 160ms ease,box-shadow 160ms ease,transform 160ms ease,translate 160ms ease,filter 160ms ease,opacity 160ms ease','important');active.push(entry);node.setAttribute('data-touch-hover','');}
    releaseTimer=setTimeout(()=>clear(),180);
  };
  document.addEventListener('pointerdown',event=>{
    if(event.pointerType!=='touch'||event.isPrimary===false)return;
    if(!needsTouchRules()){touchObserved=true;rebuild();}
    mouseHover(false);
    clear(true);
    // Cards share desktop hover feedback even when the tap lands between their links.
    // Keep the existing drag cancellation and release timing; never delay navigation.
    const control=event.target.closest('.product-card,button,a[href],summary,label,select,input,[role="button"],[role="radio"],[role="checkbox"]');
    if(!control||control.disabled||control.getAttribute('aria-disabled')==='true')return;
    pointer={id:event.pointerId,x:event.clientX,y:event.clientY,control};
    pendingTap=null;
  },{passive:true,capture:true});
  document.addEventListener('pointerup',event=>{if(pointer?.id===event.pointerId){pendingTap={control:pointer.control,until:performance.now()+700};pointer=null;}},{passive:true,capture:true});
  document.addEventListener('click',event=>{
    const tap=pendingTap;pendingTap=null;
    if(!tap||performance.now()>tap.until||event.pointerType&&event.pointerType!=='touch'||!tap.control.contains(event.target))return;
    activate(tap.control);
  },{passive:true,capture:true});
  document.addEventListener('pointermove',event=>{if(event.pointerType==='mouse'&&hoverMedia.matches)mouseHover(true);if(pointer?.id===event.pointerId&&Math.hypot(event.clientX-pointer.x,event.clientY-pointer.y)>12)clear();},{passive:true,capture:true});
  document.addEventListener('pointercancel',event=>{if(pointer?.id===event.pointerId)clear();},{passive:true,capture:true});
  window.addEventListener('blur',reset);window.addEventListener('pagehide',reset);window.addEventListener('pageshow',reset);window.addEventListener('popstate',reset);
  document.addEventListener('close',reset,true);document.addEventListener('cancel',reset,true);
  document.addEventListener('meriavo:gesturestart',reset);
  document.addEventListener('visibilitychange',()=>{if(document.hidden)reset();});
})();



/* GPT WORK: Load the passive phone tap recorder only in an explicitly requested draft test. */
(() => {
  if (new URL(location.href).searchParams.get('mv_tap_debug') !== '1' || window.Shopify?.theme?.role !== 'unpublished') return;
  const source = document.currentScript?.src;
  if (!source) return;
  const script = document.createElement('script');
  // GPT WORK: Version the opt-in recorder so a phone cannot reuse an older cached trace format.
  script.src = new URL('meriavo-tap-audit.js?v=a83d885f0236f125b58893eca202566d', source).href;
  document.head.append(script);
})();


